import React, {useEffect, useState} from "react";
import {Guest, GuestResponse, RsvpRequest} from "../../types/Guests";
import "./css/rsvp.css"
import {toast, ToastContainer} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {API_URL, EVENT_NAME, USER_ID} from "../../constants/ApiConstants";
import GuestSelector from "./components/GuestSelector/GuestSelector";
import MultiGuestSelector from "./components/MultiGuestSelector/MultiGuestSelector";
import GuestRsvp from "./components/GuestRsvp/GuestRsvp";
import {ChoiceResponse} from "../../types/Choices";
import ChoiceSelector from "./components/ChoiceSelector/ChoiceSelector";
import Input from "../../components/form/Input";
import {useNavigate} from "react-router-dom";
import Navbar from "../../components/Navbar";
import PlusOneManager from "./components/PlusOneManager/PlusOneManager";

const RSVP = () => {

    const [guest, setGuest] = useState<Guest | undefined>();
    const [rsvpMap] = useState<Map<number, RsvpRequest>>(new Map<number, RsvpRequest>());
    const [choicesMap] = useState<Map<number, ChoiceResponse>>(new Map<number, ChoiceResponse>());
    const [searchResults, setSearchResults] = useState<GuestResponse | null>(null);
    const [guests, setGuests] = useState<Guest[] | undefined>();
    const [multipleGroups, setMultipleGroups] = useState<boolean>(false);
    const [rsvpCompleted, setRsvpCompleted] = useState<boolean>(false);
    const [plusOneCompleted, setPlusOneCompleted] = useState<boolean>(false);
    const navigate = useNavigate();

    useEffect(() => {

        const fetchGuestsByGroup = async () => {

            const requestOptions: RequestInit = {
                method: "GET",
            }

            const url = new URL(`${API_URL}/users/${USER_ID}/events/${EVENT_NAME}/groups/${guest?.groupName}`);

            try {
                const response = await fetch(url, requestOptions);

                if (response.ok) {
                    setSearchResults(null);
                    setGuest(undefined);

                    return await response.json();
                } else {
                    console.log(`Guests for group ${guest?.groupName} not found`)
                    toast.error("An unexpected error has occurred");
                    setSearchResults(null);
                    setGuest(undefined);
                }
            } catch (error) {
                console.log('Error fetching guests:', error);
                toast.error("An unexpected error has occurred, please try again later");
            }
        }

        const loadChoices = (guests:Guest[]) => {
            guests.forEach((guest) => {
                fetchGuestOptions(guest.id).then((choices) => {

                    if (choices && choices.choices.length > 0) {
                        console.log(`Loaded choices for guest ${guest.id}: ${choices}`);
                        choicesMap.set(guest.id, choices);
                    }
                });
            });

            console.log(`Loaded ${choicesMap.size} choices`);

        }

        if (guest) {

            fetchGuestsByGroup().then((response:Guest[]) => {

                setGuests(response);
                loadChoices(response);

            });
        }

    }, [guest]);

    useEffect(() => {
        if (rsvpCompleted && plusOneCompleted && choicesMap.size === 0) {
            console.log("No Choices found, submitting RSVP")
            submitRsvps();
        }
    }, [rsvpCompleted, plusOneCompleted]);

    const fetchGuestOptions = async (id:number):Promise<ChoiceResponse | undefined> => {
        const requestOptions: RequestInit = {
            method: "GET",
        }

        const url = new URL(`${API_URL}/users/${USER_ID}/events/${EVENT_NAME}/guests/${id}/choices`);

        try {
            const response = await fetch(url, requestOptions);

            if (response.ok) {
                return await response.json();
            } else {
                console.log(`Choices for guest ${id} not found`)
                toast.error("An unexpected error has occurred attempting to load guest choices");
            }
        } catch (error) {
            console.log('Error fetching guest choices:', error);
            toast.error("An unexpected error has occurred, please try again later");
        }

    }

    const handlePlusOnesCreated = (newPlusOnes: Guest[]) => {
        console.log(`Received ${newPlusOnes.length} newly created plus ones`);

        // Add new plus ones to the guests array
        if (guests) {
            const updatedGuests = [...guests, ...newPlusOnes];
            setGuests(updatedGuests);

            // Initialize rsvpMap entries for new plus ones
            newPlusOnes.forEach((plusOne) => {
                // Initialize with empty optionIds array
                rsvpMap.set(plusOne.id, {
                    guestId: plusOne.id,
                    status: plusOne.status,
                    optionIds: []
                });

                // Load choices for the new plus ones
                fetchGuestOptions(plusOne.id).then((choices) => {
                    if (choices && choices.choices.length > 0) {
                        console.log(`Loaded choices for plus one ${plusOne.id}: ${plusOne.firstName} ${plusOne.lastName}`);
                        choicesMap.set(plusOne.id, choices);
                    }
                });
            });
        }
    }

    useEffect(() => {
        if (searchResults) {
            if (searchResults.items.length > 1) {
                let groups:Set<string> = new Set();
                searchResults.items.forEach((item) => {
                    if (item.groupName) {
                        console.log(`Adding group ${item.groupName}`)
                        groups.add(item.groupName);
                    }
                });

                if (groups.size > 1) {
                    console.log("multiple groups detected")
                    setMultipleGroups(true);
                    return;
                } else if (groups.size === 1) {
                    // Multiple guests in the same group - automatically select the first one
                    console.log("multiple guests in same group detected")
                    setGuest(searchResults.items[0]);
                    return;
                }
            } else if (searchResults.items.length === 1) {
                // Single guest found - automatically select it
                setGuest(searchResults.items[0]);
                return;
            }
        }

        setMultipleGroups(false);

    }, [searchResults]);

    const postRsvp = async () => {

        const rsvpArray:RsvpRequest[] = [];
        rsvpMap.forEach((value) => {
            rsvpArray.push(value);
        })

        const body = JSON.stringify(rsvpArray);

        const requestOptions: RequestInit = {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
            },
            body: body
        }

        const url = new URL(`${API_URL}/users/${USER_ID}/events/${EVENT_NAME}/rsvp`);

        try {
            const response = await fetch(url, requestOptions);

            if (response.ok) {
                toast.success("Submission successfull!")
                return await response.json();
            } else {
                console.log(`Error posting rsvp`)
                toast.error("An unexpected error has occurred");
            }
        } catch (error) {
            console.log('Error fetching guests:', error);
            toast.error("An unexpected error has occurred, please try again later");
        }
    }

    // @ts-ignore
    const handleSubmitRsvps = async (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();

        return submitRsvps();
    }

    const submitRsvps = async () => {
        let containsError = false;
        let allOptionsNotSelected = false;

        //Validate all choices are made
        if (guests) {
            guests.forEach((g) => {

                console.log(`Validating answers for guest ${g.firstName} ${g.lastName}`);

                let allChoices = choicesMap.get(g.id);
                let rsvp = rsvpMap.get(g.id);

                if (rsvp?.status === "RSVP") {

                    if (rsvp && !choicesMap.has(g.id)) {
                        console.log(`No choices found for guest ${g.firstName} ${g.lastName}`);
                    } else if (allChoices && rsvp) {
                        const currentRsvp = rsvp;
                        allChoices.choices.forEach((c) => {
                            // Skip choices whose requirements aren't met (only when there are selected options to check against)
                            if (c.requirements.length > 0 && currentRsvp.optionIds.length > 0 && !c.requirements.some(reqId => currentRsvp.optionIds.includes(reqId))) {
                                return;
                            }
                            if (c.openEnded) {
                                const hasResponse = currentRsvp.openEndedResponses?.some(
                                    r => r.choiceId === c.choiceId && r.response.trim().length > 0
                                );
                                if (!hasResponse) {
                                    console.log(`Guest ${g.firstName} ${g.lastName} has unanswered open-ended choice: ${c.choiceName}`);
                                    allOptionsNotSelected = true;
                                }
                            } else {
                                let isSelected = false;
                                let optionIds = c.options.map(c => c.id);
                                currentRsvp.optionIds.forEach((oId) => {
                                    if (optionIds.includes(oId)) {
                                        isSelected = true;
                                    }
                                })

                                if (!isSelected) {
                                    console.log(`Guest ${g.firstName} ${g.lastName} has at least one unanswered choice`);
                                    allOptionsNotSelected = true;
                                }
                            }
                        })
                    } else {
                        console.log(`Failed to find choices or rsvp request for guest ${g.firstName} ${g.lastName}`);
                    }
                }
            });

            if (containsError) {
                toast.error("An unexpected error has occurred");
                return;
            }

            if (allOptionsNotSelected) {
                toast.error("All choices must be selected");
                return;
            }

            postRsvp().then((ret) => {
                if (ret) {
                    navigate("/submission-completed");
                }
            });
        }
    }

    return (
        <main>
            <ToastContainer position="top-center" />
            <Navbar/>
            <div className="container__rsvp">
                <div className="content__rsvp">
                    <h1 className="text-cursive">rsvp</h1>
                    {
                        !guests && searchResults === null &&
                        <GuestSelector
                            setGuest={setGuest}
                            setSearchResults={setSearchResults}
                        />
                    }
                    {
                        multipleGroups && !guest &&
                        <MultiGuestSelector
                            multipleGroups={multipleGroups}
                            setGuest={setGuest}
                            searchResults={searchResults}
                        />
                    }
                    {
                        guests && guests.length > 0 && !rsvpCompleted &&
                        <GuestRsvp
                            guests={guests}
                            rsvpMap={rsvpMap}
                            setRsvpCompleted={setRsvpCompleted}
                        />
                    }
                    {
                        rsvpCompleted && !plusOneCompleted && guests &&
                        <PlusOneManager
                            guests={guests}
                            rsvpMap={rsvpMap}
                            setPlusOneCompleted={setPlusOneCompleted}
                            onPlusOnesCreated={handlePlusOnesCreated}
                        />
                    }
                    {
                        rsvpCompleted && plusOneCompleted && guests &&
                        <div className="container__choices-selection">
                            <div className="choice__selector-title">
                                <h1>Please make the following selections for each guest</h1>
                            </div>
                            {
                                guests.map((guest) => {
                                    console.log(choicesMap)
                                    if (choicesMap.has(guest.id)) {
                                        return (
                                            <ChoiceSelector
                                                key={guest.id}
                                                choices={choicesMap}
                                                guest={guest}
                                                rsvpMap={rsvpMap}
                                            />
                                        );
                                    } else {
                                        return null;
                                    }

                                })
                            }
                            <div className="choice__selector-submit">
                                <Input
                                    type={"submit"}
                                    name={"submitChoices"}
                                    value={"Submit"}
                                    onClick={handleSubmitRsvps}
                                />
                            </div>
                        </div>
                    }
                </div>
            </div>
        </main>
    );
};

export default RSVP;
